import { useEffect, useMemo, useState } from "react";
import { Navigate, useLocation, useNavigate, Link } from "react-router";
import { Grid } from "@mui/material";
import { DataTable } from "../../components/DataTable/DataTable";
import { Row } from "../../components/Row";
import { TournamentHeader } from "../../components/TournamentComponents/TournamentHeader";
import { TPageDescription } from "../../components/TournamentComponents/TPageDescription";
import { TournamentText } from "../../components/TournamentComponents/TournamentText";
import { TournamentSidebar } from "../../components/TournamentComponents/TournamentSidebar";
import {
  TournamentLink,
  TournamentPlaceContent,
} from "../../components/TournamentComponents/TournamentLink";
import { TournamentTrivia } from "../../components/TournamentComponents/TournamentTrivia";
import { TournamentFooter } from "../../components/TournamentComponents/TournamentFooter";
import { TournamentDetailDisplay } from "./TournamentDetails";
import {
  CompetitorDetails,
  CurrentTournamentContent,
} from "./CompetitorDetails";
import { useDocumentTitle } from "../../hooks/useDocumentTitle";
import { useDataProvider } from "../../hooks/dataProvider";
import { useKeyedParams } from "../../hooks/useParams";
import {
  getPage,
  setSelectedPage,
} from "../../state/actions/tournamentActions";
import {
  TournamentPage,
  TournamentSeries,
  TournamentSummary,
  TTierDescriptions,
} from "../../types/tournamentTypes";
import { getPageName } from "../../util/tournamentUtils";
import { getDateRangeDisplay } from "../../util/dateUtils";
import "./Tournaments.scss";
import { equalsIgnoreCase } from "../../util/generalUtil";

interface TournamentPageProps {
  pageId: string;
  secondaryId?: string;
}

export function TournamentPageWrapper() {
  const navigate = useNavigate();
  const location = useLocation();
  const { pageId, secondaryId } = useKeyedParams<TournamentPageProps>();
  useDocumentTitle(
    `${pageId}${
      secondaryId ? ` ${secondaryId}` : ""
    } - BQA Tournaments`.replace(/_/g, " ")
  );

  const [pageNotLoaded, setPageNotLoaded] = useState<boolean>(false);
  useEffect(() => {
    // The component doesn't unmount when the route changes, so don't continue displaying the "no page" content
    setPageNotLoaded(false);
  }, [pageId, secondaryId]);

  const id = useMemo(() => {
    const start = `${pageId}${secondaryId ? `/${secondaryId}` : ""}`;
    if (start.includes("(")) return start;
    return start.replace(/_/g, " ");
  }, [pageId, secondaryId]);

  const provider = useDataProvider({
    id,
    getId: (tournament) =>
      `${tournament.pageId}${
        tournament.secondaryId ? `/${tournament.secondaryId}` : ""
      }`,
    dispatchAction: setSelectedPage,
    selector: (state) => state.tournaments.selectedPage,
    getData: (id) => getPage(id, undefined, location.state?.redirect),
    onGetData: (page) => {
      navigate(
        `/tournaments/${page.pageId}${
          page.secondaryId ? `/${page.secondaryId}` : ""
        }`.replace(/ /g, "_"),
        {
          replace: true,
          state: {
            ...location.state,
            redirectedFrom:
              !equalsIgnoreCase(pageId, page.pageId.replace(/ /g, "_")) ||
              !equalsIgnoreCase(
                (secondaryId || "").replace(/_/g, " "),
                page.secondaryId
              )
                ? { pageId, secondaryId }
                : undefined,
          },
        }
      );
    },
    render: (page) => {
      return <TournamentPageComponent selectedPage={page} />;
    },
    onUseFallback: () => {
      if (location.state?.alternatePage && location.state?.redirect !== false) {
        const newState = {
          ...location.state,
          redirectedFrom: { pageId, secondaryId },
        };
        delete newState.alternatePage;
        navigate(
          `/tournaments/${location.state.alternatePage.pageId}${
            location.state.alternatePage.secondaryId
              ? `/${location.state.alternatePage.secondaryId}`
              : ""
          }`.replace(/ /g, "_"),
          { replace: true, state: newState }
        );
      } else {
        setPageNotLoaded(true);
      }
    },
    disableFallback: true,
  });

  if (pageNotLoaded) {
    return (
      <div className="page">
        <meta name="robots" content="noindex, nofollow" />
        <h1>
          {getPageName({ pageId, secondaryId } as TournamentPage).replace(
            /_/g,
            " "
          )}
        </h1>
        <p>
          There is no content for this page. If you think there should be,
          please add it below.
        </p>
        <Row style={{ marginTop: 10 }}>
          <button className="secondary-btn" onClick={() => navigate(-1)}>
            Go Back
          </button>
          <Link
            className="clickable"
            to="/tournaments"
            style={{ marginLeft: 10 }}
          >
            Return Home
          </Link>
        </Row>
        <TournamentFooter
          pageId={pageId}
          secondaryId={secondaryId}
          linkText="Request to add page"
        />
      </div>
    );
  }

  return provider.getPage();
}
export function TournamentPageComponent({
  selectedPage,
}: {
  selectedPage: TournamentPage;
}) {
  const location = useLocation();

  const pageName = getPageName(selectedPage);
  useDocumentTitle(pageName + " - BQA Tournaments");

  if (selectedPage.pageType === "REDIRECT") {
    if (location.state?.redirect !== false) {
      return (
        <Navigate
          to={`/tournaments/${selectedPage.redirectTo.pageId}${
            selectedPage.redirectTo.secondaryId
              ? `/${selectedPage.redirectTo.secondaryId}`
              : ""
          }`.replace(/ /g, "_")}
          state={{
            redirectedFrom: {
              pageId: selectedPage.pageId,
              secondaryId: selectedPage.secondaryId,
            },
          }}
          replace
        />
      );
    } else {
      return (
        <div className="tournament-page">
          <TournamentHeader page={selectedPage} pageName={pageName} />
          <p>
            {"This page redirects to "}
            <TournamentLink {...selectedPage.redirectTo} />
            {"."}
          </p>
        </div>
      );
    }
  }

  const isTournament = selectedPage.pageType === "TOURNAMENT";
  const isCompetitor =
    selectedPage.pageType === "QUIZZER" || selectedPage.pageType === "TEAM";

  const mainContent = (
    <>
      {selectedPage.pageType === "SERIES" && (
        <TournamentsListContent page={selectedPage} />
      )}
      {isTournament && <TournamentDetailDisplay page={selectedPage} />}
      {isCompetitor && <CompetitorDetails page={selectedPage} />}
      {selectedPage.pageType === "OTHER" && (
        <CurrentTournamentContent page={selectedPage} />
      )}
      {!isTournament && !isCompetitor && (
        <TournamentTrivia page={selectedPage} />
      )}
    </>
  );
  return (
    <div
      className="tournament-page"
      style={{
        display:
          selectedPage.pageType === "TOURNAMENT" ? "inline-block" : undefined,
        width: "100%",
      }}
    >
      <TournamentHeader page={selectedPage} pageName={pageName} />
      {location.state?.redirectedFrom && (
        <p style={{ fontSize: 14, marginBottom: 10 }}>
          {"(Redirected from "}
          <TournamentLink
            {...location.state.redirectedFrom}
            navigateState={{ redirect: false }}
          />
          {")"}
        </p>
      )}
      <TPageDescription page={selectedPage} />
      {selectedPage.additionalData.bio && (
        <TournamentText text={selectedPage.additionalData.bio} />
      )}
      {selectedPage.adjacentLinks && (
        <Grid container spacing={1} style={{ marginTop: 20, marginBottom: 20 }}>
          {selectedPage.adjacentLinks.map((link, index) => (
            <Grid key={`${link.pageId}-${link.secondaryId}`}>
              <TournamentLink
                {...link}
                text={link.title}
                key={index}
                className={
                  selectedPage.pageId === link.pageId &&
                  selectedPage.secondaryId === link.secondaryId
                    ? "check checked"
                    : "check outline"
                }
                style={{
                  width: "unset",
                  height: "unset",
                  padding: "8px 16px",
                  textDecoration: "none",
                }}
              />
            </Grid>
          ))}
        </Grid>
      )}
      {isTournament ? (
        mainContent
      ) : (
        <Grid container spacing={2}>
          <TournamentSidebar
            page={selectedPage}
            breakpoints={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 2 }}
          />
          <Grid
            size={
              selectedPage.pageType === "SERIES"
                ? undefined
                : { xs: 12, sm: 12, md: 8, lg: 8, xl: 10 }
            }
          >
            {mainContent}
          </Grid>
        </Grid>
      )}

      <TournamentFooter page={selectedPage} />
    </div>
  );
}

const TournamentsListContent = (props: { page: TournamentSeries }) => {
  const differentTiers = useMemo(() => {
    const firstTier = props.page.tournaments[0]?.tier;
    for (const t of props.page.tournaments) {
      if (t.tier !== firstTier) return true;
    }
    return false;
  }, [props.page]);
  return (
    <>
      <h3 style={{ marginTop: 0, maxHeight: "unset" }}>
        Tournaments in the Series
      </h3>
      <DataTable<TournamentSummary>
        style={{ marginBottom: 20 }}
        getKey={(obj) => `${obj.pageId}-${obj.secondaryId}`}
        columns={[
          {
            title: "Tournament",
            property: "name",
            render: (obj) => (
              <TournamentLink
                pageId={obj.pageId}
                secondaryId={obj.secondaryId}
              />
            ),
            maxWidth: 300,
          },
          {
            title: "Date",
            property: "dates",
            render: (obj) => getDateRangeDisplay(obj.startDate, obj.endDate),
            sortFunc: (obj) => obj.startDate,
            maxWidth: 250,
          },
          {
            title: "Tier",
            property: "tier",
            render: (obj) => TTierDescriptions[obj.tier],
            visible: differentTiers,
          },
          {
            title: "Teams",
            property: "teamCount",
            render: (obj) => (obj.isIndividual ? "Indiv." : obj.teamCount),
            sortFunc: true,
          },
          ...["1st", "2nd", "3rd"].map((title, index) => {
            return {
              title,
              property: `place${index + 1}`,
              render: (obj: TournamentSummary) => (
                <TournamentPlaceContent tournament={obj} index={index} />
              ),
            };
          }),
        ]}
        data={props.page.tournaments}
        defaultSort={{
          property: "dates",
          direction: "desc",
        }}
      />
    </>
  );
};
